import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { ArrowLeft } from "lucide-react";
import { USDollar } from "~/functions";

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entries = payload
    .filter((item) => item.dataKey !== "name")
    .map((item) => ({
      key: item.dataKey,
      value: item.value || 0,
      color: item.color,
      name: item.payload?.labels?.[item.dataKey]?.name || item.dataKey,
      code: item.payload?.labels?.[item.dataKey]?.code || "",
    }))
    .filter((item) => item.value !== 0);
  const total = entries.reduce((sum, item) => sum + item.value, 0);
  const barLabel = payload[0]?.payload?.name || "";
  return (
    <div className="rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-3">
      <p className="mb-1 font-semibold">{barLabel}</p>
      <p className="text-xs text-gray-500 mb-2">Total: {USDollar.format(total)}</p>
      <div className="text-sm space-y-1 max-h-40 overflow-y-auto pr-1">
        {entries.map((item) => (
          <p key={item.key} className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: item.color }}></span>
            <span className="font-medium">{item.code}:</span> {item.name} —{" "}
            {USDollar.format(item.value)}
          </p>
        ))}
      </div>
    </div>
  );
};

export default function ExpensesCompareChart({ data, onClose }) {
  if (!data || data.length === 0) return null;
  const palette = [
    "#EE5827",
    "#3B82F6",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
    "#EC4899",
    "#22C55E",
    "#0EA5E9",
    "#F97316",
    "#64748B",
  ];
  const chartData = [
    { name: "Current", labels: {} },
    { name: "Compare", labels: {} },
  ];
  data.forEach((item, index) => {
    const key = `code_${index}`;
    chartData[0][key] = item.total || 0;
    chartData[1][key] = item.compareMonth?.total || 0;
    chartData[0].labels[key] = { code: item.code, name: item.name };
    chartData[1].labels[key] = { code: item.code, name: item.name };
  });

  return (
    <div className="w-full p-4 border rounded-lg mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-lg font-bold">Expenses Comparison</h2>
        </div>
        <div className="text-sm text-gray-500">Current vs Compare</div>
      </div>

      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.15} />
          <XAxis dataKey="name" fontSize={12} interval={0} />
          <YAxis tickFormatter={(value) => USDollar.format(value)} fontSize={12} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "transparent" }} />
          {data.map((item, index) => (
            <Bar
              key={item.code || index}
              dataKey={`code_${index}`}
              stackId="total"
              fill={palette[index % palette.length]}
              radius={[0, 0, 0, 0]}
              name={`${item.code} - ${item.name}`}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
